import "./TakeawayUpdateInscription.scss";
import { useState, useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import useFirestoreErrorTranslation from "../../../../utility/useFirestoreErrorTranslation";
import useTakeawayValidationSchema from "../../../../components/validationsSchemas/useTakeawayValidationSchema";
import GreenRedOutlinedButtons from "../../../../components/buttons/greenRedOutlinedButtons/GreenRedOutlinedButtons";
import GradientButton from "../../../../components/buttons/gradientButton/GradientButton";
import PictureDisplaySelector from "../../../../components/ticketing/pictureDisplaySelector/PictureDisplaySelector";
import FormField from "../../../../components/formField/FormField";
import Map from "../../../../components/ticketing/map/Map";
import { Loader2 } from "../../../../helper/loader";
import ModalComponent from "../../../../components/modal/Modal";
import * as Yup from "yup";
import updateTakeaway from "../../../../utility/updateTakeaway";

function TakeawayUpdateInscription() {
  const [takeawayInformations, setTakeawayInformations] = useState({});
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [modalInformations, setModalInformations] = useState({
    content: "",
    title: "",
  });
  const errorTranslation = useFirestoreErrorTranslation();
  const navigate = useNavigate();
  const validationSchema = useTakeawayValidationSchema();
  const location = useLocation();
  const { t } = useTranslation("takeaway");

  const oldImageUrl = useRef(null);

  useLayoutEffect(() => {
    if (location?.state?.selectedOrg && "takeawayOpen" in location.state.selectedOrg) {
      const rawSchedule = location.state.selectedOrg.schedule;
      const transformedSchedule = {};

      for (const day in rawSchedule) {
        const entry = rawSchedule[day];

        if (Object.keys(entry).length > 0) {
          const [startHour, startMinute] = entry.open.split(":").map(Number);
          const [endHour, endMinute] = entry.close.split(":").map(Number);

          const baseDate = new Date();
          const openDate = new Date(baseDate);
          const closeDate = new Date(baseDate);
          openDate.setHours(startHour, startMinute, 0, 0);
          closeDate.setHours(endHour, endMinute, 0, 0);

          transformedSchedule[day] = {
            open: openDate,
            close: closeDate,
          };
        } else {
          transformedSchedule[day] = {};
        }
      }

      if (location.state.selectedOrg.coverUrl) {
        oldImageUrl.current = location.state.selectedOrg.coverUrl;
      }

      setTakeawayInformations({
        schedule: transformedSchedule,
        description: location.state.selectedOrg.takeawayDescription,
        location: {
          latitude: location.state.selectedOrg.location.latitude,
          longitude: location.state.selectedOrg.location.longitude,
          address: location.state.selectedOrg.location.address,
        },
        sellerPaysFees: location.state.selectedOrg.sellerPaysFees,
        takeawayOpen: location.state.selectedOrg.takeawayOpen,
        coverUrl: location.state.selectedOrg.coverUrl,
      });
    } else {
      setTakeawayInformations({
        schedule: {
          monday: {},
          tuesday: {},
          wednesday: {},
          thursday: {},
          friday: {},
          saturday: {},
          sunday: {},
        },
        description: "",
        location: {
          latitude: 50.840329972,
          longitude: 4.3499986,
          latitudeDelta: 0.05,
          longitudeDelta: 0.05,
          address: "",
        },
        sellerPaysFees: true,
        takeawayOpen: false,
      });
    }

    setIsLoading(false);
  }, []);

  async function handleOrganizationUpdate() {
    setIsLoading(true);
    setErrors({});

    const takeawayInformationsFormatted = JSON.parse(JSON.stringify(takeawayInformations));

    Object.keys(takeawayInformationsFormatted?.schedule).forEach((day) => {
      const daySchedule = takeawayInformationsFormatted.schedule[day];

      if (daySchedule && daySchedule.open && daySchedule.close) {
        const openDate = new Date(daySchedule.open);
        const closeDate = new Date(daySchedule.close);

        if (!isNaN(openDate) && !isNaN(closeDate)) {
          daySchedule.open = `${openDate.getHours().toString().padStart(2, "0")}:${openDate
            .getMinutes()
            .toString()
            .padStart(2, "0")}`;
          daySchedule.close = `${closeDate.getHours().toString().padStart(2, "0")}:${closeDate
            .getMinutes()
            .toString()
            .padStart(2, "0")}`;
        }
      }
    });

    try {
      await validationSchema.validate(takeawayInformationsFormatted, {
        abortEarly: false,
      });
      await updateTakeaway({
        orgId: location?.state?.selectedOrg.id,
        updateData: {
          schedule: takeawayInformationsFormatted.schedule,
          takeawayDescription: takeawayInformationsFormatted.description,
          location: {
            latitude: takeawayInformationsFormatted.location.latitude,
            longitude: takeawayInformationsFormatted.location.longitude,
            address: takeawayInformationsFormatted.location.address,
          },
          sellerPaysFees: takeawayInformationsFormatted.sellerPaysFees,
          takeawayOpen: takeawayInformationsFormatted.takeawayOpen,
          coverUrl: takeawayInformationsFormatted.coverUrl,
        },
        oldImageUrl: oldImageUrl.current,
      });
      const updatedOrg = {
        ...location.state.selectedOrg,
        schedule: takeawayInformationsFormatted.schedule,
        takeawayDescription: takeawayInformationsFormatted.description,
        location: {
          latitude: takeawayInformationsFormatted.location.latitude,
          longitude: takeawayInformationsFormatted.location.longitude,
          address: takeawayInformationsFormatted.location.address,
        },
        sellerPaysFees: takeawayInformationsFormatted.sellerPaysFees,
        takeawayOpen: takeawayInformationsFormatted.takeawayOpen,
        coverUrl: takeawayInformationsFormatted.coverUrl,
      };
      navigate("/dashboard/takeaway-overview", {
        state: { updatedOrg },
      });
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const formattedErrors = {};
        error.inner.forEach((err) => {
          if (err.path.includes("schedule.")) {
            const [_, day] = err.path.split(".");
            if (!formattedErrors.schedule) formattedErrors.schedule = {};
            formattedErrors.schedule[day] = err.message;
          } else {
            formattedErrors[err.path] = err.message;
          }
        });
        setModalInformations({
          title: t("error_validating_takeaway"),
          content: t("check_informations"),
        });
        setErrors(formattedErrors);
        console.error("Erreurs de validation :", error.errors);
      } else {
        setModalInformations({
          title: t("error_updating_takeaway"),
          content: errorTranslation(error),
        });
      }
    }

    setIsLoading(false);
  }

  function handlePictureChange(event) {
    const file = event.target.files[0];

    if (file) {
      if (file.type.startsWith("image/")) {
        const url = URL.createObjectURL(file);
        setTakeawayInformations((prev) => ({ ...prev, coverUrl: url }));
      }
    }
  }

  function handleDateChange({ name, isOpen, open, close }) {
    setTakeawayInformations((prev) => ({
      ...prev,
      schedule: {
        ...prev.schedule,
        [name]: !isOpen
          ? {
              open: new Date(),
              close: new Date(),
            }
          : {},
      },
    }));
  }

  function DateSelector({ name, values, error }) {
    return (
      <div className="takeaway-update-schedule-week-day">
        <ModalComponent
          isOpen={!!modalInformations.title}
          s
          onClose={() => setModalInformations({ title: "", content: "" })}
          title={modalInformations.title}
          message={modalInformations.content}
        />
        <p className="takeaway-update-schedule-week-day-name">{t(name)}</p>
        <div className="takeaway-update-schedule-week-day-time">
          <FormField
            type="time"
            value={values?.open}
            onChange={(value) =>
              setTakeawayInformations((prev) => ({
                ...prev,
                schedule: {
                  ...prev.schedule,
                  [name]: { ...prev.schedule[name], open: value },
                },
              }))
            }
          />
          <FormField
            type="time"
            value={values?.close}
            onChange={(value) =>
              setTakeawayInformations((prev) => ({
                ...prev,
                schedule: {
                  ...prev.schedule,
                  [name]: { ...prev.schedule[name], close: value },
                },
              }))
            }
          />
          <button
            className={`takeaway-update-schedule-week-day-time-button ${
              Object.keys(values).length > 0 ? "open" : "close"
            }`}
            onClick={() =>
              handleDateChange({
                name,
                values,
                isOpen: Object.keys(values).length > 0,
              })
            }
          >
            {Object.keys(values).length > 0 ? t("open") : t("Close")}
          </button>
        </div>
        {error && <p className="errorMessage">{error}</p>}
      </div>
    );
  }

  return (
    <div className="takeaway-update">
      {isLoading && <Loader2 />}
      {!isLoading && (
        <>
          <section className="takeaway-update-header">
            <h1>{t("takeawayOverview")}</h1>
            <GreenRedOutlinedButtons
              greenName={t("open")}
              redName={t("close")}
              activeGreenCondition={takeawayInformations?.takeawayOpen}
              redOnPress={() =>
                setTakeawayInformations((prev) => ({
                  ...prev,
                  takeawayOpen: false,
                }))
              }
              greenOnPress={() =>
                setTakeawayInformations((prev) => ({
                  ...prev,
                  takeawayOpen: true,
                }))
              }
            />
          </section>

          <PictureDisplaySelector
            initialMediaUrl={takeawayInformations?.coverUrl}
            onMediaChange={handlePictureChange}
            error={errors.coverUrl}
            title={location?.state?.selectedOrg.organization}
          />

          <section className="takeaway-update-description">
            <FormField
              value={takeawayInformations?.description}
              onChange={(value) =>
                setTakeawayInformations((prev) => ({
                  ...prev,
                  description: value,
                }))
              }
              type="longText"
              label={t("description")}
              error={errors.description}
            />

            <div className="takeaway-update-description-buttons">
              <p>{t("whoPaysFees")}</p>
              <div className="takeaway-update-description-buttons-wrapper">
                <button
                  className={`takeaway-update-description-buttons-wrapper ${
                    takeawayInformations?.sellerPaysFees ? "" : "active"
                  }`}
                  onClick={() =>
                    setTakeawayInformations((prev) => ({
                      ...prev,
                      sellerPaysFees: false,
                    }))
                  }
                >
                  {t("client")}
                </button>
                <button
                  className={`takeaway-update-description-buttons-wrapper ${
                    takeawayInformations?.sellerPaysFees ? "active" : ""
                  }`}
                  onClick={() =>
                    setTakeawayInformations((prev) => ({
                      ...prev,
                      sellerPaysFees: true,
                    }))
                  }
                >
                  {t("seller")}
                </button>
              </div>
            </div>
          </section>

          <section className="takeaway-update-schedule">
            <p className="takeaway-update-schedule-title">{t("schedule")}</p>
            <div className="takeaway-update-schedule-week">
              {Object.entries(takeawayInformations?.schedule || {}).map(
                ([key, content], index, array) => (
                  <div key={key}>
                    <div className="takeaway-update-schedule-week-day">
                      <DateSelector
                        name={key}
                        values={content}
                        onChange={(value) =>
                          setTakeawayInformations((prev) => ({
                            ...prev,
                            schedule: { ...prev.schedule, [key]: value },
                          }))
                        }
                        error={errors.schedule?.[key]}
                      />
                    </div>
                  </div>
                ),
              )}
            </div>
          </section>

          <Map
            marker={{
              lat: takeawayInformations?.location?.latitude || 0.123,
              lng: takeawayInformations?.location?.longitude || 0.123,
            }}
            address={takeawayInformations?.location?.address}
            setMarker={(value) =>
              setTakeawayInformations((prev) => ({
                ...prev,
                location: { ...prev.location, ...value },
              }))
            }
            minHeight={window.innerWidth < 768 ? "300px" : null}
            maxHeight={window.innerWidth < 768 ? "450px" : null}
          />

          <section className="takeaway-update-validation">
            <GradientButton onPress={handleOrganizationUpdate}>
              {t("updateInscription")}
            </GradientButton>
          </section>
        </>
      )}
    </div>
  );
}

export default TakeawayUpdateInscription;
